import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search, Sparkles, Loader2, Users, CheckCircle2, ArrowRight, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const PIPELINE_STAGES = [
    { key: "screened", label: "Add to pipeline" },
    { key: "interview", label: "Move to Interview" },
    { key: "offer", label: "Move to Offer" },
    { key: "hired", label: "Mark Hired" },
    { key: "passed", label: "Pass" },
];

const CandidateCard = ({ c, onAdd, onRequest, alreadyInPipeline, alreadyRequested }) => (
    <motion.div
        initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
        data-testid={`candidate-${c.candidate_id}`}
        className="card-soft p-5 flex flex-col group hover:border-emerald-500/40 transition-colors"
    >
        <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
                <h3 className="font-heading font-semibold text-foreground leading-snug">{c.name || "Anonymous candidate"}</h3>
                {c.headline && <div className="text-xs text-muted-foreground line-clamp-2 mt-1">{c.headline}</div>}
            </div>
            <div className="text-right shrink-0">
                <div className="mono-score text-2xl font-bold text-emerald-600 dark:text-emerald-400">{c.match_score}%</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">match</div>
            </div>
        </div>
        <div className="mt-3 text-xs text-muted-foreground">
            Best fit for <span className="font-semibold text-foreground">{c.job_title}</span> · {c.company}
        </div>
        {c.fit_reasons?.length > 0 && (
            <ul className="mt-3 space-y-1 flex-1">
                {c.fit_reasons.slice(0, 3).map((r, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                        <CheckCircle2 size={11} className="text-emerald-500 mt-0.5 shrink-0" /> {r}
                    </li>
                ))}
            </ul>
        )}
        <div className="mt-auto pt-4 border-t border-border flex items-center justify-between gap-2 flex-wrap">
            {alreadyInPipeline ? (
                <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 border text-[10px]">In pipeline</Badge>
            ) : (
                <button
                    onClick={() => onAdd(c)}
                    data-testid={`add-pipeline-${c.candidate_id}`}
                    className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1"
                >
                    <Plus size={12} /> Add to pipeline
                </button>
            )}
            <button
                onClick={() => onRequest(c)}
                disabled={alreadyRequested}
                data-testid={`request-passport-${c.candidate_id}`}
                className="btn-green text-xs px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-60"
            >
                {alreadyRequested ? "Requested" : "Request Passport"} <ArrowRight size={11} className="ml-0.5" />
            </button>
        </div>
    </motion.div>
);

const FindTalent = () => {
    const [candidates, setCandidates] = useState([]);
    const [pipeline, setPipeline] = useState([]);
    const [requests, setRequests] = useState([]);
    const [q, setQ] = useState("");
    const [minScore, setMinScore] = useState("0");
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const [c, p, r] = await Promise.all([
                api.get("/employer/candidates"),
                api.get("/employer/pipeline"),
                api.get("/employer/passport-requests"),
            ]);
            setCandidates(c.data);
            setPipeline(p.data);
            setRequests(r.data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const pipelineKeys = useMemo(() => new Set(pipeline.map((p) => `${p.brief_id}:${p.candidate_id}`)), [pipeline]);
    const requestedKeys = useMemo(() => new Set(requests.map((r) => `${r.brief_id}:${r.candidate_id}`)), [requests]);

    const filtered = useMemo(() => candidates.filter((c) => {
        const matchesQ = !q || `${c.name} ${c.headline} ${c.job_title}`.toLowerCase().includes(q.toLowerCase());
        const matchesScore = c.match_score >= Number(minScore);
        return matchesQ && matchesScore;
    }), [candidates, q, minScore]);

    const addPipeline = async (c) => {
        try {
            await api.post("/employer/pipeline", { brief_id: c.job_id, candidate_id: c.candidate_id, stage: "screened" });
            toast.success(`${c.name} added to pipeline.`);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const requestPassport = async (c) => {
        try {
            await api.post("/employer/passport-requests", { brief_id: c.job_id, candidate_id: c.candidate_id, candidate_name: c.name });
            toast.success("Passport requested — our team will follow up.");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div data-testid="find-talent-page">
            <div className="mb-6">
                <p className="overline">AI-matched talent</p>
                <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">Find Talent</h1>
                <p className="text-sm text-muted-foreground mt-1">Every candidate surfaced by your AI Hiring Briefs, deduped and ranked.</p>
            </div>
            <div className="mb-6 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
                <div className="relative">
                    <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, headline, role…" data-testid="find-talent-search" className="pl-10 h-11" />
                </div>
                <div className="w-40">
                    <Select value={minScore} onValueChange={setMinScore}>
                        <SelectTrigger data-testid="find-talent-min-score" className="h-11"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="0">Any match</SelectItem>
                            <SelectItem value="50">50%+</SelectItem>
                            <SelectItem value="70">70%+</SelectItem>
                            <SelectItem value="85">85%+</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : filtered.length === 0 ? (
                <div className="card-soft p-12 text-center">
                    <Users size={22} className="mx-auto text-emerald-500" />
                    <h3 className="mt-3 font-heading font-semibold text-xl text-foreground">No matched candidates yet.</h3>
                    <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">Create an AI Hiring Brief first — the second it&apos;s ready, your top matches appear here.</p>
                    <a href="/employer/briefs#new" className="btn-green mt-6 inline-flex text-sm">
                        Create Hiring Brief <Sparkles size={13} className="ml-2" />
                    </a>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filtered.map((c) => (
                        <CandidateCard
                            key={`${c.job_id}:${c.candidate_id}`}
                            c={c}
                            onAdd={addPipeline}
                            onRequest={requestPassport}
                            alreadyInPipeline={pipelineKeys.has(`${c.job_id}:${c.candidate_id}`)}
                            alreadyRequested={requestedKeys.has(`${c.job_id}:${c.candidate_id}`)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default FindTalent;
